import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { translateCol } from "../../custom/columnI18n";
import { uzmiGresku } from "../../custom/greske";
import { Toast } from "primereact/toast";
import Loading from "../../Loading/loading";
import "./info.css";
import { AuthContext } from "../../Header/auth - context";
import { useSelector, useDispatch } from "react-redux";
import APP_VERSION from "../../version";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";

/* Pragovi očitavanja i filter „šta uopšte ulazi u spisak" stoje van komponente
   jer ih koristi i efekat koji šalje broj u badge — a on se izvršava pre nego
   što se telo komponente ponovo pročita. */
const PRAG_PRIKAZA_DANA = 15;
const PRAG_UPOZORENJA_DANA = 7;

const zaOcitavanje = (rows) =>
    (rows || []).filter(r => r.LastDownload == null || (r.DanaDoRoka != null && r.DanaDoRoka <= PRAG_PRIKAZA_DANA));

/* Polje za napomenu uz jedan alarm.

   Zasebna komponenta NAMERNO: tekst se drzi u njenom lokalnom stanju, pa svaki
   otkucaj iscrtava samo ovu celiju. Dok je stanje stajalo na nivou strane, svaki
   znak je ponovo iscrtavao ceo DataTable i polje bi izgubilo fokus — izgledalo
   je kao da se nista ne moze uneti.

   Snimanje je izricito (dugme ili Enter). NE snima se na izlazak iz polja: klik
   na dugme prvo izazove izlazak, pa bi se ista napomena slala dvaput. */
const PoljeNapomene = ({ red, naslovDugmeta, mestoDrzac, onSacuvaj }) => {
    const sacuvana = red?.Napomena ?? "";
    const [tekst, setTekst] = React.useState(sacuvana);

    /* Kad spisak stigne iznova (osvezavanje, promena firme), polje prati novu
       sacuvanu vrednost — ali samo ako korisnik nema nesnimljenu izmenu. */
    React.useEffect(() => { setTekst(sacuvana); }, [sacuvana]);

    const nesnimljeno = String(tekst).trim() !== String(sacuvana).trim();

    return (
        <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
            <input
                type="text"
                className="p-inputtext p-component"
                style={{ width: "100%", minWidth: "160px" }}
                value={tekst}
                placeholder={mestoDrzac}
                onChange={(e) => setTekst(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") onSacuvaj(red, String(tekst).trim());
                    if (e.key === "Escape") setTekst(sacuvana);
                }}
            />
            <Button
                type="button"
                icon="pi pi-save"
                rounded
                severity={nesnimljeno ? "success" : "secondary"}
                disabled={!nesnimljeno}
                className="p-button-sm"
                tooltip={naslovDugmeta}
                tooltipOptions={{ position: "left" }}
                onClick={() => onSacuvaj(red, String(tekst).trim())}
            />
        </div>
    );
};

const Info = () => {
  const { t } = useTranslation();
  const company = useSelector((state) => state.company);
  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const toast = useRef(null);
  const [load, setLoad] = useState(true);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceGlobalFilter, setInvoiceGlobalFilter] = useState(null);

  const INVOICE_COLS = ["Broj fakture", "Klijent", "Grupa fakture", "Datum / Valuta", "Rok placanja", "Proverena", "Operater"];
  const INVOICE_HIDDEN = ["ID", "StatusProvere", "Prekoracen"];

  const [sefData, setSefData] = useState([]);
  const [tahoKarticeFilter, setTahoKarticeFilter] = useState(null);
  const [tahoVozilaFilter, setTahoVozilaFilter] = useState(null);
  const [sefGlobalFilter, setSefGlobalFilter] = useState(null);

  const [driverData, setDriverData] = useState([]);
  const [driverGlobalFilter, setDriverGlobalFilter] = useState(null);

  /* Rokovi koje firma sama unese (Administracija → Firma → Alarmi). */
  const [firmaData, setFirmaData] = useState([]);
  const [firmaGlobalFilter, setFirmaGlobalFilter] = useState(null);

  const [tahoVozaci, setTahoVozaci] = useState([]);
  const [tahoVozila, setTahoVozila] = useState([]);

  // Prozori koji se prikazuju (multiselect): niz od 'VOZILA' | 'VOZAC' | 'FAKTURE' | 'SEF'
  const [views, setViews] = useState(['VOZILA']);
  // panel koji je raširen preko cele širine (null = svi u dve kolone)
  const [prosiren, setProsiren] = useState(null);
  const sadrzajRef = useRef(null);

  /* Overlay je apsolutno pozicioniran unutar skrolabilnog sadržaja, pa mu se
     vrh vezuje za vrh SADRŽAJA, a ne za vidljivi deo. Zato se pri širenju
     kontejner vrati na vrh — tek tada overlay poklapa ekran. */
  const prikaziPanel = (kljuc) => {
    setProsiren((prev) => (prev === kljuc ? null : kljuc));
    if (sadrzajRef.current) sadrzajRef.current.scrollTop = 0;
  };

  const SEF_COLS = ["Broj fakture", "Klijent", "Grupa fakture", "Datum kreiranja", "BrojDana", "Operater"];

  useEffect(() => {
    const headers = { "Content-Type": "application/json", Authorization: "Bearer " + auth.token };
    const req = (url) => fetch(process.env.REACT_APP_BACKEND_URL + url, { method: "POST", headers }).then(handleErrors);

    Promise.all([req("/alarms"), req("/alarms/invoicealarms"), req("/alarms/sefalarms"), req("/alarms/drivers"), req("/tacho/cardDownloads").catch(()=>[]), req("/tacho/vehicleDownloads").catch(()=>[]), req("/alarms/company").catch(()=>[])])
      .then(([vehicleData, invoiceRows, sefRows, driverRows, cardRows, vuRows, firmaRows]) => {
        setFirmaData(Array.isArray(firmaRows) ? firmaRows : []);
        setTahoVozaci(Array.isArray(cardRows) ? cardRows : []);
        setTahoVozila(Array.isArray(vuRows) ? vuRows : []);
        const vehicles = vehicleData || [];
        const invoices = invoiceRows || [];
        const sefInvoices = sefRows || [];
        const drivers = driverRows || [];

        setTableData(vehicles);
        if (vehicles.length > 0) {
          const columns = Object.keys(vehicles[0])
            .filter((key) => !["ID"].includes(key))
            .map((key) => ({ label: key, value: key }));
          setAllColumns(columns);
          setSelectedColumns(columns.slice(0, 10).map((col) => col.value));
        }

        setInvoiceData(invoices);
        setSefData(sefInvoices);
        setDriverData(drivers);
        /* Novi podaci — stara pozicija u paginatoru moze da pokazuje na stranu
           koje vise nema, pa bi tabela izgledala prazno. */
        setPrviRed({});
        /* Badge broji SVE grupe iz padajućeg spiska. Ranije su nedostajala dva
           očitavanja (taho kartice i uređaji), pa je broj po otvaranju Info-a bio
           manji od zbira grupa koje se odmah ispod vide. */
        dispatch({
          type: "COUNTER",
          value: vehicles.length + drivers.length + invoices.length + sefInvoices.length
               + zaOcitavanje(cardRows).length + zaOcitavanje(vuRows).length
               + (Array.isArray(firmaRows) ? firmaRows.length : 0),
        });
      })
      .catch((error) => {
        error.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "Error", detail: e.message, life: 3000 })
        );
      })
      .finally(() => setLoad(false));
  }, []);

  const extractEndDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr).trim();
    const parts = s.split("-").map((p) => p.trim());
    return parts.length >= 2 ? parts[parts.length - 1] : s;
  };

  const parseSRDate = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = String(dateStr).trim().split(".");
    if (!day || !month || !year) return null;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(d.getTime()) ? null : d;
  };

  const daysUntil = (dateStr) => {
    const end = extractEndDate(dateStr); // <-- KLJUČNO
    const target = parseSRDate(end);
    if (!target) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  };

  const coloringParts = (dateStr) => {
    const diff = daysUntil(dateStr);
    if (diff === null) return "transparent";
    if (diff <= 0) return "#F96565";
    if (diff <= 7) return "rgb(181, 177, 49)";
    return "transparent";
  };

  const sizingDate = (dateStr) => {
    const diff = daysUntil(dateStr);
    if (diff === null) return "14px";
    if (diff <= 7) return "20px";
    return "14px";
  };

  const textColor = (dateStr) => {
    const diff = daysUntil(dateStr);
    if (diff === null) return "black";
    if (diff <= 7) return "white";
    return "black";
  };

  const formatMinus30 = (dateStr) => {
    const end = extractEndDate(dateStr); // uzmi datumDo ako je range
    const d = parseSRDate(end);
    if (!d) return dateStr;

    const before = new Date(d);
    before.setDate(before.getDate() - 30);

    const fmt = (x) =>
      `${String(x.getDate()).padStart(2, "0")}.${String(
        x.getMonth() + 1
      ).padStart(2, "0")}.${x.getFullYear()}`;

    return `${fmt(before)} - ${fmt(d)}`;
  };

  /* Snimanje je izričito — dugmetom pored polja ili Enterom.

     Tabela se posle uspešnog upisa NE povlači ponovo — dovoljno je osvežiti
     jedan red u memoriji, pa se ne gubi pozicija u spisku ni tekst u drugim
     poljima koja korisnik možda upravo popunjava. */
  const sacuvajNapomenu = (red, tekst) =>
    sacuvajNapomenuOpsti({ red, tekst, ruta: "/alarms/saveNote", postavi: setTableData });

  /* Napomena uz alarm vozača. Ista logika kao kod vozila, samo druga ruta i
     drugi spisak — zato je izdvojena zajednička funkcija ispod. */
  const sacuvajNapomenuOpsti = async ({ red, tekst, ruta, postavi }) => {
    if (String(red?.Napomena ?? "") === String(tekst ?? "")) return;   // nema izmene

    try {
      const odgovor = await fetch(process.env.REACT_APP_BACKEND_URL + ruta, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        body: JSON.stringify({ id: red.ID, napomena: tekst }),
      });
      if (!odgovor.ok) throw odgovor;

      const telo = await odgovor.text();
      const podaci = telo ? JSON.parse(telo) : [];

      /* Poruka stiže samo kad alarm više ne postoji — dokument je u međuvremenu
         obnovljen pa ga je noćni posao sklonio. */
      if (podaci?.[0]?.message) {
        toast.current?.show({ severity: "warn", summary: "Upozorenje!", detail: podaci[0].message, life: 5000 });
        return;
      }

      /* Osvežava se samo taj jedan red u memoriji — spisak se ne povlači ponovo,
         pa se ne gubi pozicija ni tekst u drugim poljima. */
      postavi((prev) => (prev || []).map(r => r.ID === red.ID ? { ...r, Napomena: tekst } : r));
    } catch (e) {
      const g = await uzmiGresku(e, "Napomena nije sačuvana.");
      toast.current?.show({ severity: "error", summary: "Greška", detail: g.message, life: 5000 });
    }
  };

  const sacuvajNapomenuVozaca = (red, tekst) =>
    sacuvajNapomenuOpsti({ red, tekst, ruta: "/alarms/saveDriverNote", postavi: setDriverData });

  /* Jedno mesto na kome se odlučuje šta se ZA JEDNU ĆELIJU prikazuje — koriste ga
     i tabela i izvoz u Excel. Ranije je svako računao za sebe, pa je šestomesečni
     u tabeli imao raspon (±15 dana oko roka), a u Excelu goli datum iz baze. */
  const vrednostAlarma = (rowData, colKey) =>
    colKey === "Datum" && rowData.Dokument === "Šestomesečni"
      ? formatMinus30(rowData.Datum)
      : rowData[colKey] ?? "";

  /* Rokovi očitavanja tahografa: kartica vozača 28 dana, jedinica vozila 90 dana.
     Prikazuju se samo oni kojima je do roka ostalo najviše 15 dana (uključujući istekle
     i one koji nikad nisu očitani). Boja reda:
       beo  = ostalo 8–15 dana
       žut  = ostalo 0–7 dana
       crven = rok istekao ILI nikad očitano */
  const formatDatuma = (val) => {
    if (!val) return '—';
    const s = String(val).replace('T', ' ').substring(0, 16);
    const [dat, vre] = s.split(' ');
    if (!dat) return '—';
    const [y, mo, d] = dat.split('-');
    return vre ? `${d}.${mo}.${y} ${vre}` : `${d}.${mo}.${y}`;
  };

  const bojaRedaOcitavanja = (r) => {
    if (r.LastDownload == null) return 'row-taho-expired';            // nikad očitano
    if (r.DanaDoRoka != null && r.DanaDoRoka < 0) return 'row-taho-expired';
    if (r.DanaDoRoka != null && r.DanaDoRoka <= PRAG_UPOZORENJA_DANA) return 'row-taho-warn';
    return '';
  };

  const tahoKarticeZaPrikaz = zaOcitavanje(tahoVozaci);
  const tahoVozilaZaPrikaz  = zaOcitavanje(tahoVozila);

  const statusOcitavanja = (r) => {
    if (r.NemaKarticu) return t('taho.nemaBrojKartice');
    if (r.LastDownload == null) return t('taho.nemaOcitavanja');
    if (r.DanaDoRoka < 0) return `${t('taho.rokIstekao')} (${Math.abs(r.DanaDoRoka)})`;
    return `${t('taho.uRedu')} (${r.DanaDoRoka})`;
  };

  const handleErrors = (response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw response;
    }
  };

  const exportExcel = async () => {
    if (!selectedColumns || selectedColumns.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Alarmi");

    const headers = selectedColumns;

    // Add header row (translated display, data keys stay raw)
    const headerRow = worksheet.addRow(headers.map((h) => translateCol(t, h)));
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFEFEF" },
      };
      cell.border = {
        top: { style: "medium" },
        left: { style: "medium" },
        bottom: { style: "medium" },
        right: { style: "medium" },
      };
    });

    // Add filtered data rows
    tableData.forEach((rowData) => {
      const row = worksheet.addRow(headers.map((h) => vrednostAlarma(rowData, h)));
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Auto-size columns
    worksheet.columns.forEach((column, i) => {
      const lengths = [
        headers[i].length,
        ...tableData.map((row) => (row[headers[i]] ?? "").toString().length),
      ];
      column.width = Math.max(...lengths) + 2;
    });

    // Serbian-formatted datetime
    const date = new Date();
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Belgrade",
    };
    const serbianDateTime = date
      .toLocaleString("sr-RS", options)
      .replace(/\s/g, " ")
      .replace(/,/g, "");

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Alarmi - ${company.companyName} - ${serbianDateTime}.xlsx`);
  };

  const exportExcelInvoice = async () => {
    if (!invoiceData || invoiceData.length === 0) return;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Alarmi fakture");
    const headerRow = worksheet.addRow(INVOICE_COLS.map((h) => translateCol(t, h)));
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
      cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
    });
    invoiceData.forEach((rowData) => {
      const row = worksheet.addRow(INVOICE_COLS.map((h) => rowData[h] ?? ""));
      row.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const lengths = [INVOICE_COLS[i].length, ...invoiceData.map((r) => (r[INVOICE_COLS[i]] ?? "").toString().length)];
      col.width = Math.min(Math.max(...lengths) + 2, 40);
    });
    const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Alarmi fakture - ${company.companyName} - ${dt}.xlsx`);
  };

  /* Fajl na disku (C:/Dokumenta/...) -> URL koji servira backend (/API/files/...) */
  const linkPriloga = (putanja) => {
    if (!putanja) return null;
    const normalizovana = String(putanja).replace(/\\/g, "/");
    const relativna = normalizovana
      .replace(/^([A-Za-z]:)?\/Dokumenta\//, "")
      .replace(/^\/var\/www\/dokumenta\//, "");
    return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(relativna)}`;
  };

  const exportExcelFirma = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Alarmi firme");
    /* Prevodi se prikaz zaglavlja, a podaci se čitaju po SIROVOM imenu kolone. */
    const KOLONE = ["Naziv", "Važi do", "Preostalo dana", "Napomena"];
    const headerRow = worksheet.addRow(KOLONE.map((h) => translateCol(t, h)));
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
      cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
    });
    (firmaData || []).forEach((r) => {
      const row = worksheet.addRow(KOLONE.map((h) => r[h] ?? ""));
      row.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const lengths = [KOLONE[i].length, ...(firmaData || []).map((r) => (r[KOLONE[i]] ?? "").toString().length)];
      col.width = Math.min(Math.max(...lengths) + 2, 40);
    });
    const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Alarmi firme - ${company.companyName} - ${dt}.xlsx`);
  };

  const exportExcelSef = async () => {
    if (!sefData || sefData.length === 0) return;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Alarmi SEF");
    const headerRow = worksheet.addRow(SEF_COLS.map((h) => (h === "BrojDana" ? translateCol(t, "Broj dana") : translateCol(t, h))));
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
      cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
    });
    sefData.forEach((rowData) => {
      const row = worksheet.addRow(SEF_COLS.map((h) => rowData[h] ?? ""));
      row.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const lengths = [SEF_COLS[i].length, ...sefData.map((r) => (r[SEF_COLS[i]] ?? "").toString().length)];
      col.width = Math.min(Math.max(...lengths) + 2, 40);
    });
    const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Alarmi SEF - ${company.companyName} - ${dt}.xlsx`);
  };

  /* Zajednički izvoz za alarme očitavanja — kolone se prosleđuju kao
     [{ header, value }] jer se vrednosti formatiraju (datum, status). */
  const exportOcitavanja = async (redovi, kolone, naziv) => {
    if (!redovi || redovi.length === 0) return;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(naziv.substring(0, 30));
    const headerRow = worksheet.addRow(kolone.map((c) => c.header));
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
      cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
    });
    redovi.forEach((r) => {
      const row = worksheet.addRow(kolone.map((c) => c.value(r)));
      row.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const lengths = [kolone[i].header.length, ...redovi.map((r) => String(kolone[i].value(r) ?? "").length)];
      col.width = Math.min(Math.max(...lengths) + 2, 40);
    });
    const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${naziv} - ${company.companyName} - ${dt}.xlsx`);
  };

  const headerOcitavanja = (onExport, filterSetter) => (
    <div className="export-buttons fakture-stavke-header">
      <Button type="button" icon="pi pi-file-excel" severity="success" rounded
        onClick={onExport} data-pr-tooltip="XLS" />
      <input type="search" onInput={(e) => filterSetter(e.target.value)}
        placeholder={t('common.pretraga')}
        className="p-inputtext p-component" />
    </div>
  );

  /* Raširen panel ima mnogo više prostora — prikazuje i više redova po strani. */
  /* Stranicanje po tabeli.

     Cim se prosledi onPage, DataTable prelazi u KONTROLISAN rezim i od tada
     cita i "first" i "rows" iz propsa — pa se oba moraju drzati ovde. Da se
     prati samo "first", izbor broja redova bi se vracao na staro pri svakom
     iscrtavanju. */
  const REDOVA_PO_STRANI = [25, 50, 100];

  const [redovaPoStrani, setRedovaPoStrani] = useState({});
  const [prviRed, setPrviRed] = useState({});

  /* Prosiren panel pokazuje duplo vise redova — dok korisnik sam ne izabere u
     paginatoru; tada vazi njegov izbor i sirenje ga vise ne menja. */
  const brojRedova = (kljuc) => redovaPoStrani[kljuc] ?? (prosiren === kljuc ? 50 : 25);

  const promenaStrane = (kljuc) => (e) => {
    setPrviRed((prev) => ({ ...prev, [kljuc]: e.first }));
    setRedovaPoStrani((prev) => ({ ...prev, [kljuc]: e.rows }));
  };

  /* Zajednicki propsi paginatora — da se sest tabela ne razidju. */
  const propsiStranicenja = (kljuc) => ({
    paginator: true,
    rows: brojRedova(kljuc),
    rowsPerPageOptions: REDOVA_PO_STRANI,
    first: prviRed[kljuc] ?? 0,
    onPage: promenaStrane(kljuc),
  });

  /* Zaglavlje panela: naslov levo, dugme za širenje/sužavanje skroz desno. */
  const zaglavljePanela = (naslov, kljuc) => (
    <div className="info_alarm_header">
      <h4>{naslov}</h4>
      <Button
        type="button" text rounded className="info-expand-btn"
        icon={prosiren === kljuc ? 'pi pi-window-minimize' : 'pi pi-window-maximize'}
        tooltip={prosiren === kljuc ? t('info.suziTabelu') : t('info.prosiriTabelu')}
        tooltipOptions={{ position: 'left' }}
        onClick={() => prikaziPanel(kljuc)}
      />
    </div>
  );

  const header = (
    <div className="export-buttons fakture-stavke-header">
      <Button
        type="button"
        icon="pi pi-file-excel"
        severity="success"
        rounded
        onClick={exportExcel}
        data-pr-tooltip="XLS"
      />
      <input
        type="search"
        onInput={(e) => setGlobalFilter(e.target.value)}
        placeholder={t('nova.phPretraga')}
        className="p-inputtext p-component"
      />
    </div>
  );

  return (
    <div id="info_main">
      {load && <Loading />}
      {/* Naslov i izbor prozora dele istu traku — tabele tako dobijaju
          punu preostalu visinu ekrana. */}
      <div className="info">
        <p>{t('info.naslovSistem')}</p>
        <div className="info-view-select">
          <MultiSelect
            value={views}
            onChange={(e) => setViews(e.value)}
            style={{ minWidth: "320px", maxWidth: "100%" }}
            placeholder={t('info.izaberiProzore')}
            maxSelectedLabels={2}
            selectedItemsLabel={`${views.length} ${t('info.izabranoProzora')}`}
            options={[
              { label: `${t('info.alarmiVozila')} (${(tableData || []).length})`, value: "VOZILA" },
              { label: `${t('info.alarmiVozaca').toUpperCase()} (${driverData.length})`, value: "VOZAC" },
              { label: `${t('info.alarmiFaktureNenaplacene')} (${invoiceData.length})`, value: "FAKTURE" },
              { label: `${t('info.alarmiFaktureSef')} (${sefData.length})`, value: "SEF" },
              { label: `${t('info.alarmOcitavanjaKartica').toUpperCase()} (${tahoKarticeZaPrikaz.length})`, value: "TAHO_VOZACI" },
              { label: `${t('info.alarmOcitavanjaVozila').toUpperCase()} (${tahoVozilaZaPrikaz.length})`, value: "TAHO_VOZILA" },
              { label: `${t('info.alarmiFirme')} (${firmaData.length})`, value: "FIRMA" },
            ]}
          />
        </div>
      </div>
      <Toast ref={toast} />
      <div ref={sadrzajRef} className={'info_content' + (prosiren ? ' info_content--overlay' : '')}>
        <div className="info_tables_row">
        {views.includes("VOZILA") && (
        <div className={'info_alarm' + (prosiren === 'VOZILA' ? ' info_alarm--wide' : '')}>
          {zaglavljePanela(t('info.alarmiVozila'), 'VOZILA')}
          <section>
            <Tooltip target=".export-buttons>button" position="bottom" />
            <DataTable
              style={{ width: "100%" }}
              value={tableData || []}
              {...propsiStranicenja('VOZILA')}
              globalFilter={globalFilter}
              scrollable
              header={header}
              scrollHeight="flex"
              emptyMessage={t('common.noDataTable')}
              resizableColumns
              className="turePregledTable stavke-tabela"
              columnResizeMode="expand"
            >
              {selectedColumns.map((colKey) => {
                const colMeta = allColumns.find((col) => col.value === colKey);
                return (
                  <Column
                    key={colKey}
                    field={colKey}
                    header={translateCol(t, colMeta?.label || colKey)}
                    sortable
                    body={(rowData) => {
                      const rawValue = rowData[colKey];

                      /* Napomena je jedina kolona koja se i upisuje — polje ima svoju
                         komponentu, sa lokalnim stanjem i izričitim snimanjem. */
                      if (colKey === "Napomena") {
                        return (
                          <PoljeNapomene
                            red={rowData}
                            mestoDrzac={t('info.napomenaPlaceholder')}
                            naslovDugmeta={t('info.napomenaSacuvaj')}
                            onSacuvaj={sacuvajNapomenu}
                          />
                        );
                      }

                      const displayValue =
                        vrednostAlarma(rowData, colKey) || (rawValue ?? "-");
                      if (colKey !== "Datum") {
                        return (
                          <div
                            style={{
                              padding: "0.5rem",
                              boxSizing: "border-box",
                            }}
                          >
                            {displayValue}
                          </div>
                        );
                      }
                      const dateForColoring =
                        rowData.Dokument === "Šestomesečni"
                          ? formatMinus30(rowData.Datum)
                          : rowData.Datum;

                      const bgColor = coloringParts(dateForColoring);
                      const size = sizingDate(dateForColoring);
                      const txColor = textColor(dateForColoring);

                      return (
                        <div
                          style={{
                            backgroundColor: bgColor,
                            width: "100%",
                            height: "100%",
                            padding: "0.5rem",
                            boxSizing: "border-box",
                            borderRadius: "4px",
                            fontSize: size,
                            color: txColor,
                          }}
                        >
                          {displayValue}
                        </div>
                      );
                    }}
                  />
                );
              })}
            </DataTable>
          </section>
        </div>
        )}

        {/* ── Vozači (driver) alarms table ── */}
        {views.includes("VOZAC") && (
        <div className={'info_alarm' + (prosiren === 'VOZAC' ? ' info_alarm--wide' : '')}>
          {zaglavljePanela(t('info.alarmiVozaca').toUpperCase(), 'VOZAC')}
          <section>
            <DataTable
              style={{ width: "100%" }}
              value={driverData}
              {...propsiStranicenja('VOZAC')}
              globalFilter={driverGlobalFilter}
              scrollable
              scrollHeight="flex"
              emptyMessage={t('common.noDataTable')}
              resizableColumns
              columnResizeMode="expand"
              className="turePregledTable stavke-tabela"
              header={headerOcitavanja(
                () => exportOcitavanja(driverData, [
                  { header: t('alarmi.vozac'), value: r => r.Vozac ?? '' },
                  { header: t('alarmi.dokument'), value: r => translateCol(t, r.Dokument) ?? '' },
                  { header: t('nova.datum'), value: r => r.Datum ?? '' },
                  { header: translateCol(t, 'Napomena'), value: r => r.Napomena ?? '' },
                ], 'Alarmi vozaca'),
                setDriverGlobalFilter)}
            >
              <Column field="Vozac" header={t('alarmi.vozac')} sortable style={{ minWidth: "160px" }}
                body={(row) => <div style={{ padding: "0.5rem" }}>{row.Vozac ?? "-"}</div>} />
              <Column field="Dokument" header={t('alarmi.dokument')} sortable style={{ minWidth: "160px" }}
                body={(row) => <div style={{ padding: "0.5rem" }}>{translateCol(t, row.Dokument) ?? "-"}</div>} />
              <Column field="Datum" header={t('nova.datum')} sortable style={{ minWidth: "120px" }}
                body={(row) => {
                  const bg = coloringParts(row.Datum);
                  const size = sizingDate(row.Datum);
                  const tx = textColor(row.Datum);
                  return (
                    <div style={{ backgroundColor: bg, width: "100%", height: "100%", padding: "0.5rem", boxSizing: "border-box", borderRadius: "4px", fontSize: size, color: tx }}>
                      {row.Datum ?? "-"}
                    </div>
                  );
                }} />
              <Column field="Napomena" header={translateCol(t, 'Napomena')} style={{ minWidth: "220px" }}
                body={(row) => (
                  <PoljeNapomene
                    red={row}
                    mestoDrzac={t('info.napomenaPlaceholder')}
                    naslovDugmeta={t('info.napomenaSacuvaj')}
                    onSacuvaj={sacuvajNapomenuVozaca}
                  />
                )} />
            </DataTable>
          </section>
        </div>
        )}

        {/* ── Invoice alarms table ── */}
        {views.includes("FAKTURE") && (
        <div className={'info_alarm' + (prosiren === 'FAKTURE' ? ' info_alarm--wide' : '')}>
          {zaglavljePanela(t('info.alarmiFaktureNenaplacene'), 'FAKTURE')}
          <section>
            <DataTable
              style={{ width: "100%" }}
              value={invoiceData}
              {...propsiStranicenja('FAKTURE')}
              globalFilter={invoiceGlobalFilter}
              scrollable
              scrollHeight="flex"
              emptyMessage={t('info.nemaNenaplacenih')}
              resizableColumns
              columnResizeMode="expand"
              rowClassName={(row) => {
                if (row.Prekoracen === 1) return "row-invoice-overdue";
                if (!row.StatusProvere) return "row-invoice-unverified";
                return "";
              }}
              header={
                <div className="export-buttons fakture-stavke-header stavke-tabela">
                  <Button
                    type="button"
                    icon="pi pi-file-excel"
                    severity="success"
                    rounded
                    onClick={exportExcelInvoice}
                    data-pr-tooltip="XLS"
                  />
                  <input
                    type="search"
                    onInput={(e) => setInvoiceGlobalFilter(e.target.value)}
                    placeholder={t('home.pretragaFaktura')}
                    className="p-inputtext p-component"
                  />
                </div>
              }
            >
              {INVOICE_COLS.map((col) => (
                <Column
                  key={col}
                  field={col}
                  header={translateCol(t, col)}
                  sortable
                  style={{ minWidth: "120px" }}
                  body={(row) => (
                    <div style={{ padding: "0.5rem", boxSizing: "border-box" }}>
                      {row[col] ?? "-"}
                    </div>
                  )}
                />
              ))}
            </DataTable>
          </section>
        </div>
        )}

        {/* ── SEF alarms table ── */}
        {views.includes("SEF") && (
        <div className={'info_alarm' + (prosiren === 'SEF' ? ' info_alarm--wide' : '')}>
          {zaglavljePanela(t('info.alarmiFaktureSef'), 'SEF')}
          <section>
            <DataTable
              style={{ width: "100%" }}
              value={sefData}
              {...propsiStranicenja('SEF')}
              globalFilter={sefGlobalFilter}
              scrollable
              scrollHeight="flex"
              emptyMessage={t('info.nemaSefCekaju')}
              resizableColumns
              columnResizeMode="expand"
              rowClassName={() => "row-invoice-overdue"}
              header={
                <div className="export-buttons fakture-stavke-header stavke-tabela">
                  <Button
                    type="button"
                    icon="pi pi-file-excel"
                    severity="success"
                    rounded
                    onClick={exportExcelSef}
                    data-pr-tooltip="XLS"
                  />
                  <input
                    type="search"
                    onInput={(e) => setSefGlobalFilter(e.target.value)}
                    placeholder={t('home.pretragaFaktura')}
                    className="p-inputtext p-component"
                  />
                </div>
              }
            >
              {SEF_COLS.map((col) => (
                <Column
                  key={col}
                  field={col}
                  header={translateCol(t, col === "BrojDana" ? "Broj dana" : col)}
                  sortable
                  style={{ minWidth: "120px" }}
                  body={(row) => (
                    <div style={{ padding: "0.5rem", boxSizing: "border-box" }}>
                      {row[col] ?? "-"}
                    </div>
                  )}
                />
              ))}
            </DataTable>
          </section>
        </div>
        )}
        {/* ── Alarm očitavanja taho kartica vozača (rok 28 dana) ── */}
        {views.includes("TAHO_VOZACI") && (
        <div className={'info_alarm' + (prosiren === 'TAHO_VOZACI' ? ' info_alarm--wide' : '')}>
          {zaglavljePanela(t('info.alarmOcitavanjaKartica').toUpperCase(), 'TAHO_VOZACI')}
          <section>
            <DataTable
              style={{ width: "100%" }} value={tahoKarticeZaPrikaz}
              {...propsiStranicenja('TAHO_VOZACI')} scrollable scrollHeight="flex"
              globalFilter={tahoKarticeFilter}
              emptyMessage={t('info.nemaZaOcitavanje')} resizableColumns columnResizeMode="expand"
              className="turePregledTable stavke-tabela" sortField="DanaDoRoka" sortOrder={1}
              rowClassName={bojaRedaOcitavanja}
              header={headerOcitavanja(
                () => exportOcitavanja(tahoKarticeZaPrikaz, [
                  { header: t('ture.vozac'), value: r => r.HolderName || '' },
                  { header: t('mk.kartica'), value: r => r.CardNumber || '' },
                  { header: t('taho.poslednjeOcitavanje'), value: r => (r.LastDownload ? formatDatuma(r.LastDownload) : '') },
                  { header: t('taho.danaDoRoka'), value: r => (r.DanaDoRoka == null ? '' : r.DanaDoRoka) },
                  { header: t('taho.status'), value: r => statusOcitavanja(r) },
                ], 'Alarm ocitavanja kartica vozaca'),
                setTahoKarticeFilter)}
            >
              <Column field="HolderName" header={t('ture.vozac')} sortable style={{ minWidth: "180px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.HolderName || '—'}</div>} />
              <Column field="CardNumber" header={t('mk.kartica')} sortable style={{ minWidth: "160px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.CardNumber || '—'}</div>} />
              <Column field="LastDownload" header={t('taho.poslednjeOcitavanje')} sortable style={{ minWidth: "140px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.LastDownload ? formatDatuma(r.LastDownload) : '—'}</div>} />
              <Column field="DanaDoRoka" header={t('taho.danaDoRoka')} sortable style={{ minWidth: "110px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.DanaDoRoka == null ? '—' : r.DanaDoRoka}</div>} />
              <Column header={t('taho.status')} style={{ minWidth: "150px" }}
                body={(r) => <div style={{ padding: "0.5rem", fontWeight: 600 }}>{statusOcitavanja(r)}</div>} />
            </DataTable>
          </section>
        </div>
        )}

        {/* ── Alarm očitavanja taho uređaja vozila (rok 90 dana) ── */}
        {views.includes("TAHO_VOZILA") && (
        <div className={'info_alarm' + (prosiren === 'TAHO_VOZILA' ? ' info_alarm--wide' : '')}>
          {zaglavljePanela(t('info.alarmOcitavanjaVozila').toUpperCase(), 'TAHO_VOZILA')}
          <section>
            <DataTable
              style={{ width: "100%" }} value={tahoVozilaZaPrikaz}
              {...propsiStranicenja('TAHO_VOZILA')} scrollable scrollHeight="flex"
              globalFilter={tahoVozilaFilter}
              emptyMessage={t('info.nemaZaOcitavanje')} resizableColumns columnResizeMode="expand"
              className="turePregledTable stavke-tabela" sortField="DanaDoRoka" sortOrder={1}
              rowClassName={bojaRedaOcitavanja}
              header={headerOcitavanja(
                () => exportOcitavanja(tahoVozilaZaPrikaz, [
                  { header: t('ture.registracija'), value: r => r.Registracija || '' },
                  { header: t('taho.brojSasije'), value: r => r.VIN || '' },
                  { header: t('taho.poslednjeOcitavanje'), value: r => (r.LastDownload ? formatDatuma(r.LastDownload) : '') },
                  { header: t('taho.danaDoRoka'), value: r => (r.DanaDoRoka == null ? '' : r.DanaDoRoka) },
                  { header: t('taho.status'), value: r => statusOcitavanja(r) },
                ], 'Alarm ocitavanja vozila'),
                setTahoVozilaFilter)}
            >
              <Column field="Registracija" header={t('ture.registracija')} sortable style={{ minWidth: "150px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.Registracija || '—'}</div>} />
              <Column field="VIN" header={t('taho.brojSasije')} sortable style={{ minWidth: "180px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.VIN || '—'}</div>} />
              <Column field="LastDownload" header={t('taho.poslednjeOcitavanje')} sortable style={{ minWidth: "140px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.LastDownload ? formatDatuma(r.LastDownload) : '—'}</div>} />
              <Column field="DanaDoRoka" header={t('taho.danaDoRoka')} sortable style={{ minWidth: "110px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.DanaDoRoka == null ? '—' : r.DanaDoRoka}</div>} />
              <Column header={t('taho.status')} style={{ minWidth: "150px" }}
                body={(r) => <div style={{ padding: "0.5rem", fontWeight: 600 }}>{statusOcitavanja(r)}</div>} />
            </DataTable>
          </section>
        </div>
        )}

        {/* ── Sopstveni alarmi firme (Administracija → Firma → Alarmi) ──
            Jedini panel u Info-u čiji sadržaj firma sama unosi: zakup, članarina,
            licenca — rokovi koje sistem inače nigde ne vodi. */}
        {views.includes("FIRMA") && (
        <div className={'info_alarm' + (prosiren === 'FIRMA' ? ' info_alarm--wide' : '')}>
          {zaglavljePanela(t('info.alarmiFirme'), 'FIRMA')}
          <section>
            <DataTable
              style={{ width: "100%" }}
              value={firmaData}
              {...propsiStranicenja('FIRMA')}
              globalFilter={firmaGlobalFilter}
              scrollable
              scrollHeight="flex"
              emptyMessage={t('firmaAlarm.nemaAlarma')}
              resizableColumns
              columnResizeMode="expand"
              sortField="Preostalo dana"
              sortOrder={1}
              /* Istekao rok je crven, rok u narednih 30 dana žut — ista skala
                 koju ostali paneli u Info-u već koriste. */
              rowClassName={(r) => {
                const d = Number(r['Preostalo dana']);
                if (d < 0) return "row-invoice-overdue";
                if (d <= 30) return "row-invoice-unverified";
                return "";
              }}
              header={
                <div className="export-buttons fakture-stavke-header stavke-tabela">
                  <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcelFirma} data-pr-tooltip="XLS" />
                  <input
                    type="search"
                    onInput={(e) => setFirmaGlobalFilter(e.target.value)}
                    placeholder={t('nova.phPretraga')}
                    className="p-inputtext p-component"
                  />
                </div>
              }
            >
              <Column field="Naziv" header={translateCol(t, 'Naziv')} sortable style={{ minWidth: "200px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.Naziv || '—'}</div>} />
              <Column field="Važi do" header={translateCol(t, 'Važi do')} sortable style={{ minWidth: "120px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r['Važi do'] || '—'}</div>} />
              <Column field="Preostalo dana" header={translateCol(t, 'Preostalo dana')} sortable style={{ minWidth: "130px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r['Preostalo dana'] == null ? '—' : r['Preostalo dana']}</div>} />
              <Column field="Napomena" header={translateCol(t, 'Napomena')} style={{ minWidth: "220px" }}
                body={(r) => <div style={{ padding: "0.5rem" }}>{r.Napomena || '—'}</div>} />
              <Column header={translateCol(t, 'Prilog')} style={{ minWidth: "120px" }}
                body={(r) => (r.Fajl
                  ? <Button label={t('firmaAlarm.otvori')} icon="pi pi-paperclip" type="button"
                      className="p-button-text p-button-sm"
                      onClick={() => window.open(linkPriloga(r.Fajl), '_blank')} />
                  : <div style={{ padding: "0.5rem" }}>—</div>)} />
            </DataTable>
          </section>
        </div>
        )}
        </div>{/* info_tables_row */}
      </div>
      <div className="footer">
        © 2025 DOO ŠARČEVIĆ CO VRBAS. Sva prava zadržana. - by Miloš Krstić &nbsp;|&nbsp; <b>verzija aplikacije {APP_VERSION}</b>
      </div>
    </div>
  );
};

export default Info;
